import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../../../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiSearch, FiEye, FiCheck, FiX, FiClock, FiAlertCircle, FiFile, FiDownload, FiPlus, FiRotateCcw, FiMail } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import TableComp, { Column } from '../../../../components/Table';
import {
    fetchCompanyIdAndPsoInfo,
    fetchBranches,
    fetchManagedPsos,
    fetchRequests,
    createRequest,
    updateRequestStatus,
    forwardRequestToHead,
    fetchAvailableCompanies,
    fetchGuardRate,
    updateGuardRate,
    type Request
} from '../service/requestManagementService';
import { fetchPSOEmail, sendEmailsWithErrorHandling, validateEmails } from '../../../../api/emailService';

/** Shared styles for email recipient chips (approve / reject / resend modals). */
const recipientChipClass =
    'inline-flex max-w-full min-w-0 items-start gap-2 rounded-lg border border-slate-200/90 bg-white py-2 pl-2.5 pr-1.5 text-left shadow-sm ring-1 ring-slate-100/80 dark:border-slate-600 dark:bg-slate-800/90 dark:ring-slate-700/80';
const recipientChipTextClass =
    'min-w-0 max-w-full flex-1 break-words text-xs font-medium leading-relaxed text-slate-800 dark:text-slate-100';

function escapeHtml(text: string): string {
    if (!text) return '';
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function getEmailDateFields(request: Request): { dateFrom: unknown; dateTo: unknown } {
    const requestType = request.requestType?.toLowerCase();
    if (requestType === 'guard') {
        return {
            dateFrom: request.guardDateFrom || request.cctvDateFrom || (request as any).dateFrom,
            dateTo: request.guardDateTo || request.cctvDateTo || (request as any).dateTo,
        };
    }
    if (requestType === 'bams') {
        return {
            dateFrom: request.bamsDateFrom || request.cctvDateFrom || (request as any).dateFrom,
            dateTo: request.bamsDateTo || request.cctvDateTo || (request as any).dateTo,
        };
    }
    return {
        dateFrom: request.cctvDateFrom || (request as any).dateFrom,
        dateTo: request.cctvDateTo || (request as any).dateTo,
    };
}

function parseEmailDateValue(date: unknown): Date | null {
    if (!date) return null;
    try {
        let d: Date;
        if (typeof date === 'object' && date !== null && 'toDate' in date && typeof (date as { toDate: () => Date }).toDate === 'function') {
            d = (date as { toDate: () => Date }).toDate();
        } else if (typeof date === 'object' && date !== null && 'seconds' in date) {
            d = new Date((date as { seconds: number }).seconds * 1000);
        } else {
            d = new Date(date as string | number);
        }
        if (Number.isNaN(d.getTime())) return null;
        return d;
    } catch {
        return null;
    }
}

function formatEmailDateOnly(date: unknown): string {
    const d = parseEmailDateValue(date);
    if (!d) return 'N/A';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

function formatEmailDateIso(date: unknown): string {
    const d = parseEmailDateValue(date);
    if (!d) return 'N/A';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
}

function pickEmailText(...vals: unknown[]): string {
    for (const v of vals) {
        if (v == null) continue;
        const s = String(v).trim();
        if (s) return s;
    }
    return '';
}

/**
 * Branch label for email — same idea as Guard table / form (`branchName || address`).
 * Avoid using `location` as address; it is often region and made CCTV rows look wrong.
 */
function getEmailBranchNameWithAddress(request: Request): string {
    const r = request as Record<string, unknown>;
    const primary = pickEmailText(
        request.branchName,
        r.branchTitle,
        r.name,
        r.branch_title
    );
    const secondary = pickEmailText(r.branchAddress, r.address, r.branchLocation);
    if (primary && secondary && primary !== secondary) return `${primary} (${secondary})`;
    return pickEmailText(primary, secondary) || 'N/A';
}

/** Inclusive day span between from/to — aligned with requestManagementService guard billing. */
function formatInclusiveNoOfDays(dateFrom: unknown, dateTo: unknown): string {
    const a = parseEmailDateValue(dateFrom);
    const b = parseEmailDateValue(dateTo);
    if (!a || !b) return 'N/A';
    const diffTime = Math.abs(b.getTime() - a.getTime());
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return String(days);
}

function formatEmailGeneratedOn(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function resolveActionAt(request: Request, explicit?: Date): Date {
    if (explicit) return explicit;
    const u = request.updatedAt;
    if (u && typeof u === 'object') {
        if ('toDate' in u && typeof (u as { toDate: () => Date }).toDate === 'function') {
            return (u as { toDate: () => Date }).toDate();
        }
        if ('seconds' in u) {
            return new Date((u as { seconds: number }).seconds * 1000);
        }
    }
    return new Date();
}

function buildRequestDetailsTableBlock(request: Request): { sectionTitle: string; tableHtml: string } {
    const dates = getEmailDateFields(request);
    const df = formatEmailDateOnly(dates.dateFrom);
    const dt = formatEmailDateOnly(dates.dateTo);
    const noOfDays = escapeHtml(formatInclusiveNoOfDays(dates.dateFrom, dates.dateTo));
    const branchName = escapeHtml(getEmailBranchNameWithAddress(request));
    const branchCode = escapeHtml(pickEmailText(request.branchCode, (request as any).branch_code) || 'N/A');
    const purposeRaw = pickEmailText(
        (request as any).purposeOfFootage,
        request.purpose,
        request.purposeOther,
        (request as any).purpose_other
    );
    const purpose = escapeHtml(purposeRaw || 'N/A');
    const rt = (request.requestType || '').toLowerCase();
    const td = 'border:1px solid #d1d5db;padding:10px 8px;font-size:12px;color:#333;';
    const th = 'border:1px solid #1e3a5f;background:#1e3a5f;color:#fff;padding:10px 8px;font-size:11px;font-weight:bold;text-align:left;';

    if (rt === 'guard') {
        const guards = escapeHtml(String(request.numberOfGuards ?? 'N/A'));
        return {
            sectionTitle: 'DETAILS OF GUARD REQUEST',
            tableHtml: `
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #d1d5db;">
<tr>
<th style="${th}">BRANCH CODE</th><th style="${th}">NAME OF BRANCH</th><th style="${th}">NO. OF GUARDS</th>
<th style="${th}">DATE FROM</th><th style="${th}">DATE TO</th><th style="${th}">NO. OF DAYS</th><th style="${th}">PURPOSE</th>
</tr>
<tr>
<td style="${td}">${branchCode}</td><td style="${td}">${branchName}</td><td style="${td}">${guards}</td>
<td style="${td}">${escapeHtml(df)}</td><td style="${td}">${escapeHtml(dt)}</td><td style="${td}">${noOfDays}</td><td style="${td}">${purpose}</td>
</tr>
</table>`,
        };
    }

    if (rt === 'bams') {
        const hw = escapeHtml(String(request.hardwareName || 'N/A'));
        return {
            sectionTitle: 'DETAILS OF BAMS REQUEST',
            tableHtml: `
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #d1d5db;">
<tr>
<th style="${th}">BRANCH CODE</th><th style="${th}">NAME OF BRANCH</th><th style="${th}">DATE FROM</th><th style="${th}">DATE TO</th>
<th style="${th}">NO. OF DAYS</th><th style="${th}">HARDWARE</th><th style="${th}">PURPOSE</th>
</tr>
<tr>
<td style="${td}">${branchCode}</td><td style="${td}">${branchName}</td><td style="${td}">${escapeHtml(df)}</td><td style="${td}">${escapeHtml(dt)}</td>
<td style="${td}">${noOfDays}</td><td style="${td}">${hw}</td><td style="${td}">${purpose}</td>
</tr>
</table>`,
        };
    }

    const timeFrom = escapeHtml(
        pickEmailText((request as any).cctvTimeFrom, request.timeFrom, (request as any).time_from) || 'N/A'
    );
    const timeTo = escapeHtml(
        pickEmailText((request as any).cctvTimeTo, request.timeTo, (request as any).time_to) || 'N/A'
    );
    const dateFromIso = escapeHtml(formatEmailDateIso(dates.dateFrom));
    const dateToIso = escapeHtml(formatEmailDateIso(dates.dateTo));
    const thSub = `${th};text-align:center;font-size:10px;`;
    const colCount = 6;
    return {
        sectionTitle: 'Details of CCTV Footage',
        tableHtml: `
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #d1d5db;">
<tr>
<th rowspan="2" style="${th};vertical-align:middle;">BRANCH CODE</th>
<th rowspan="2" style="${th};vertical-align:middle;">NAME OF BRANCH</th>
<th colspan="2" style="${th};text-align:center;">TIME</th>
<th colspan="2" style="${th};text-align:center;">DATE</th>
</tr>
<tr>
<th style="${thSub}">FROM</th><th style="${thSub}">TO</th>
<th style="${thSub}">FROM</th><th style="${thSub}">TO</th>
</tr>
<tr>
<td style="${td}">${branchCode}</td>
<td style="${td}">${branchName}</td>
<td style="${td}">${timeFrom}</td>
<td style="${td}">${timeTo}</td>
<td style="${td}">${dateFromIso}</td>
<td style="${td}">${dateToIso}</td>
</tr>
<tr>
<td colspan="${colCount}" style="${td};background:#f8fafc;"><strong>Purpose:</strong> ${purpose}</td>
</tr>
</table>`,
    };
}

/** HBL-style HTML (table layout) — matches PSO Request System Support branding. */
function buildPsoRequestStatusEmailHtml(request: Request, variant: 'approved' | 'rejected', actionAt?: Date): string {
    const id = escapeHtml(String(request.requestId || request.id || ''));
    const rtRaw = (request.requestType || 'REQUEST').toUpperCase();
    const rt = escapeHtml(rtRaw);
    const bannerTitle = variant === 'approved' ? `${rtRaw} APPROVAL` : `${rtRaw} REJECTION`;
    const statusLabel = variant === 'approved' ? 'APPROVED' : 'REJECTED';
    const statusBg = variant === 'approved' ? '#2e7d32' : '#c62828';
    const dateFieldLabel = variant === 'approved' ? 'Approval Date' : 'Rejection Date';
    const actionDateStr = formatEmailDateOnly(resolveActionAt(request, actionAt));
    const generatedOn = formatEmailGeneratedOn(new Date());
    const intro =
        variant === 'approved'
            ? `The following ${rt} request has been successfully processed.`
            : `The following ${rt} request has been <strong>rejected</strong>.`;

    const { sectionTitle, tableHtml } = buildRequestDetailsTableBlock(request);
    const sectionEsc = escapeHtml(sectionTitle);

    return `<!DOCTYPE html>
<html><body style="margin:0;padding:0;background:#eef1f5;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#eef1f5;padding:24px 12px;">
<tr><td align="center">
<table role="presentation" width="640" cellpadding="0" cellspacing="0" style="max-width:640px;width:100%;background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e2e8f0;">
<tr><td align="center" style="padding:24px 20px 8px;">
<div style="color:#00838f;font-size:28px;font-weight:800;letter-spacing:2px;line-height:1;">HBL</div>
<div style="color:#1e3a5f;font-size:11px;font-weight:700;letter-spacing:3px;margin-top:6px;">HBL PSO SUPPORT</div>
</td></tr>
<tr><td style="background:#1e5aa8;background:linear-gradient(135deg,#1e5aa8 0%,#2d7bc4 100%);padding:22px 24px;text-align:center;">
<div style="color:#ffffff;font-size:20px;font-weight:800;letter-spacing:1px;line-height:1.2;">${escapeHtml(bannerTitle)}</div>
<div style="color:#e3f2fd;font-size:14px;margin-top:10px;font-weight:600;">Request ID: ${id}</div>
</td></tr>
<tr><td style="padding:24px 22px;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#333;line-height:1.55;">
<p style="margin:0 0 10px;">Dear Team,</p>
<p style="margin:0 0 20px;">${intro}</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;border-radius:6px;margin-bottom:20px;">
<tr><td style="padding:16px 18px;">
<strong style="color:#475569;">Status:</strong>
<span style="display:inline-block;margin-left:10px;padding:7px 16px;background:${statusBg};color:#ffffff;font-weight:800;font-size:11px;letter-spacing:1px;border-radius:999px;">${statusLabel}</span>
<div style="margin-top:12px;font-size:14px;color:#334155;"><strong>${dateFieldLabel}:</strong> <span style="color:#0f172a;font-weight:600;">${escapeHtml(actionDateStr)}</span></div>
</td></tr>
</table>
<div style="background:#1e3a5f;color:#ffffff;font-weight:700;font-size:12px;padding:11px 14px;letter-spacing:0.5px;">${sectionEsc}</div>
${tableHtml}
</td></tr>
<tr><td style="padding:22px 20px;text-align:center;font-size:11px;color:#64748b;border-top:1px solid #e2e8f0;font-family:Arial,Helvetica,sans-serif;">
<div style="font-weight:700;color:#475569;margin-bottom:6px;letter-spacing:1px;">HBL PSO SUPPORT</div>
<div>This is an automated email from PSO Request Management System.</div>
<div style="margin-top:8px;color:#475569;">Generated on: ${escapeHtml(generatedOn)}</div>
<div style="margin-top:14px;font-size:10px;color:#94a3b8;">PSO Request System Support</div>
</td></tr>
</table>
</td></tr>
</table>
</body></html>`;
}

function buildRequestApprovalEmailHtml(request: Request, actionAt?: Date): string {
    return buildPsoRequestStatusEmailHtml(request, 'approved', actionAt);
}

function buildRequestRejectionEmailHtml(request: Request, actionAt?: Date): string {
    return buildPsoRequestStatusEmailHtml(request, 'rejected', actionAt);
}

async function getDefaultRecipientsForRequest(request: Request, companyId: string): Promise<string[]> {
    const emails: string[] = [];
    const contact = (request.requesterContact || '').trim();
    if (contact) {
        emails.push(...validateEmails(contact).valid);
    }
    if (request.createdBy) {
        try {
            const psoMail = await fetchPSOEmail(db, companyId, request.createdBy);
            if (psoMail && psoMail.trim()) {
                emails.push(psoMail.trim());
            }
        } catch (e) {
            console.error('Error fetching creator email:', e);
        }
    }
    return Array.from(new Set(emails.filter(Boolean)));
}

export default function RequestForm() {
    const pso = useAppSelector((s) => s.user.pso);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [psoId, setPsoId] = useState<string | null>(null);
    const [psoDesignation, setPsoDesignation] = useState<string | null>(null);
    const [managedPsoIds, setManagedPsoIds] = useState<string[]>([]);

    // Check if user can create requests
    // Regular PSO can create requests, but for incharge users, only Incharge MIS can create
    const designation = pso?.designation || psoDesignation || '';
    const isIncharge = /incharge/i.test(designation);
    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isInchargeMIS = inchargeType.includes('mis');
    const isInchargeCCTV = inchargeType.includes('cctv');
    const isInchargeGuard = inchargeType.includes('guarding') || inchargeType.includes('guard');
    const isInchargeBAMS = inchargeType.includes('bams');
    const [isBankUser, setIsBankUser] = useState(false);
    const canCreateRequests = !isIncharge || isInchargeMIS; // Regular PSO or Incharge MIS can create

    // Determine automatic request type filter for incharge users
    // PSO and Incharge MIS see all requests, other incharge types see only their type
    const getAutoRequestTypeFilter = (): 'All' | 'CCTV' | 'Guard' | 'BAMS' => {
        if (!designation || !isIncharge || isInchargeMIS) {
            return 'All'; // PSO and Incharge MIS see all, or if designation not loaded yet
        }
        if (isInchargeCCTV) {
            return 'CCTV'; // Incharge CCTV sees only CCTV requests
        }
        if (isInchargeGuard) {
            return 'Guard'; // Incharge Guard sees only Guard requests
        }
        if (isInchargeBAMS) {
            return 'BAMS'; // Incharge BAMS sees only BAMS requests
        }
        return 'All'; // Default
    };
    const [requests, setRequests] = useState<Request[]>([]);
    const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [dateFromFilter, setDateFromFilter] = useState<string>('');
    const [dateToFilter, setDateToFilter] = useState<string>('');
    // Default filter status: 'pending' for Regional PSO, 'All' for others
    const [filterStatus, setFilterStatus] = useState('All');
    const [requestTypeFilter, setRequestTypeFilter] = useState<'All' | 'CCTV' | 'Guard' | 'BAMS'>('All');
    const [statuses, setStatuses] = useState<string[]>([]);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Add Request Form states
    const [showAddModal, setShowAddModal] = useState(false);
    const [psoName, setPsoName] = useState('');
    const [psoEmail, setPsoEmail] = useState('');
    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
    const [branchSearch, setBranchSearch] = useState('');
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
    const [companySearch, setCompanySearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [branchDetailsCache, setBranchDetailsCache] = useState<Record<string, any>>({});
    const [locationDetailsCache, setLocationDetailsCache] = useState<Record<string, any>>({});
    const [displayBranchName, setDisplayBranchName] = useState<string>('N/A');
    const [displayRegionName, setDisplayRegionName] = useState<string>('N/A');

    // Guard Rate State
    const [guardRate, setGuardRate] = useState<number>(0);
    const [showRateModal, setShowRateModal] = useState(false);
    const [newRate, setNewRate] = useState<string>('');
    const [settingRate, setSettingRate] = useState(false);

    const [showApproveEmailStep, setShowApproveEmailStep] = useState(false);
    const [pendingApproveRequest, setPendingApproveRequest] = useState<Request | null>(null);
    const [approveRecipients, setApproveRecipients] = useState<string[]>([]);
    const [approveEmailDraft, setApproveEmailDraft] = useState('');
    const [approveLoadingRecipients, setApproveLoadingRecipients] = useState(false);
    const [approveSending, setApproveSending] = useState(false);

    const [showRejectEmailStep, setShowRejectEmailStep] = useState(false);
    const [pendingRejectRequest, setPendingRejectRequest] = useState<Request | null>(null);
    const [rejectRecipients, setRejectRecipients] = useState<string[]>([]);
    const [rejectEmailDraft, setRejectEmailDraft] = useState('');
    const [rejectLoadingRecipients, setRejectLoadingRecipients] = useState(false);
    const [rejectSending, setRejectSending] = useState(false);

    const [showStatusEmailModal, setShowStatusEmailModal] = useState(false);
    const [statusEmailRequest, setStatusEmailRequest] = useState<Request | null>(null);
    const [statusEmailRecipients, setStatusEmailRecipients] = useState<string[]>([]);
    const [statusEmailDraft, setStatusEmailDraft] = useState('');
    const [statusEmailLoadingDefaults, setStatusEmailLoadingDefaults] = useState(false);
    const [statusEmailSending, setStatusEmailSending] = useState(false);

    // Form fields
    const [formData, setFormData] = useState({
        requestType: '',
        branchCode: '',
        branchName: '',
        company: '',
        selectedCompanyId: '',
        requesterName: '',
        requesterContact: '',
        requiredBy: '',
        cctvDateFrom: '',
        cctvDateTo: '',
        timeFrom: '',
        timeTo: '',
        numberOfGuards: '',
        guardCompany: '',
        hardwareName: '',
        purpose: '',
        purposeOther: '',
        requestId: '',
    });

    const requiredByOptions = ['HR', 'Admin', 'Police', 'FIA', 'FRMU', 'DRU', 'CIU', 'CIO', 'Region/Branch', 'Audit', 'Others'];
    // Show all request types for Incharge MIS, otherwise show only CCTV and Guard
    const requestTypeOptions = isInchargeMIS ? ['CCTV', 'Guard', 'BAMS'] : ['CCTV', 'Guard'];
    const purposeOptionsCCTV = ['ATM transection Issue', 'HR Enquiry', 'Audit observation', 'Police Investigation', 'Other'];
    const purposeOptionsGuard = ['BAMS Failure', 'Electrical Issue', 'Renovation Work', 'BISP Camp', 'Shutter Out of order', 'Other'];

    const uid = auth.currentUser?.uid;

    // Step 1: Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
        const isBank = userRole === 'bank';
        setIsBankUser(isBank);

        const loadCompanyInfo = async () => {
            try {
                if (isBank) {
                    // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                    const directBank = await getDoc(doc(db, "banks", uid));
                    if (directBank.exists()) {
                        setCompanyId(uid);
                        return;
                    }
                    // Try query by userId field
                    const banksSnap = await getDocs(collection(db, "banks"));
                    for (const bankDoc of banksSnap.docs) {
                        const bankData = bankDoc.data();
                        if (bankData.userId === uid) {
                            setCompanyId(bankDoc.id);
                            return;
                        }
                    }
                } else {
                    // PSO or Incharge users
                    const companyInfo = await fetchCompanyIdAndPsoInfo(uid);
                    if (companyInfo) {
                        setCompanyId(companyInfo.companyId);
                        setPsoId(companyInfo.psoId);
                        setPsoDesignation(companyInfo.psoDesignation);
                        setPsoName(companyInfo.psoName);
                        setPsoEmail(companyInfo.psoEmail);
                    }
                }
            } catch (err) {
                console.error('Error fetching company info:', err);
            }
        };

        loadCompanyInfo();
    }, [uid]);

    // Fetch branches for add request modal
    // For Regional PSO and Head PSO, fetch all branches since they can see requests from other PSOs
    useEffect(() => {
        if (!companyId || !uid || !psoId || !psoDesignation) return;

        const loadBranches = async () => {
            try {
                // For Regional PSO and Head PSO, we need all branches to display branch names for requests from other PSOs
                // For regular PSO, we only need their own branches
                if (psoDesignation === 'Regional PSO' || psoDesignation === 'Head PSO') {
                    // Fetch all branches for the company
                    const branchSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
                    const allBranches = branchSnap.docs.map(doc => ({
                        id: doc.id,
                        companyId,
                        ...doc.data(),
                    }));
                    setBranches(allBranches);
                } else {
                    // Regular PSO - only fetch their own branches
                    const branchesList = await fetchBranches(companyId, psoId);
                    setBranches(branchesList);
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        };

        loadBranches();
    }, [companyId, uid, psoId, psoDesignation]);

    // Step 2: Fetch managed PSOs (if Regional PSO or Head PSO)
    useEffect(() => {
        if (!companyId || !psoId || !psoDesignation || !uid) return;

        const loadManagedPsos = async () => {
            try {
                const managedIds = await fetchManagedPsos(companyId, psoId, psoDesignation, uid);
                setManagedPsoIds(managedIds);
            } catch (error) {
                console.error('Error fetching managed PSOs:', error);
                setManagedPsoIds([]);
            }
        };

        loadManagedPsos();
    }, [companyId, psoId, psoDesignation, uid]);

    // Step 2.5: Fetch Guard Rate
    useEffect(() => {
        if (!uid) return;
        const loadRate = async () => {
            const rate = await fetchGuardRate();
            setGuardRate(rate);
            setNewRate(rate.toString());
        };
        loadRate();
    }, [uid, showRateModal]);

    // Set default filter status to 'pending' for Regional PSO
    useEffect(() => {
        if (psoDesignation === 'Regional PSO') {
            setFilterStatus('pending');
        }
    }, [psoDesignation]);

    // Step 3: Fetch requests
    useEffect(() => {
        if (!companyId) return;

        const loadRequests = async () => {
            setLoading(true);
            try {
                const requestsList = await fetchRequests(companyId, psoId || '', psoDesignation || '', managedPsoIds);
                setRequests(requestsList);

                // Extract unique statuses
                const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
                setStatuses(statusList);
            } catch (err) {
                console.error(err);
                setToastMessage('Failed to fetch requests');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadRequests();
    }, [companyId, psoId, managedPsoIds, psoDesignation]);

    // Get date fields based on request type
    const getDateFields = (request: Request) => {
        const requestType = request.requestType?.toLowerCase();

        if (requestType === 'guard') {
            return {
                dateFrom: request.guardDateFrom || request.cctvDateFrom || request.dateFrom,
                dateTo: request.guardDateTo || request.cctvDateTo || request.dateTo
            };
        } else if (requestType === 'bams') {
            return {
                dateFrom: request.bamsDateFrom || request.cctvDateFrom || request.dateFrom,
                dateTo: request.bamsDateTo || request.cctvDateTo || request.dateTo
            };
        } else {
            // CCTV or default
            return {
                dateFrom: request.cctvDateFrom || request.dateFrom,
                dateTo: request.cctvDateTo || request.dateTo
            };
        }
    };

    // Filter requests
    const filteredRequests = requests.filter(request => {
        const requestId = request.requestId || '';
        const branchCode = request.branchCode || '';
        const requesterName = request.requesterName || '';

        const matchesSearch = !searchTerm ||
            requestId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            branchCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
            requesterName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'All' || request.status === filterStatus;

        // For incharge users (except MIS), automatically filter by their type
        // For PSO and Incharge MIS, use the selected filter
        const autoFilter = getAutoRequestTypeFilter();
        const effectiveFilter = (isIncharge && !isInchargeMIS && !!designation) ? autoFilter : requestTypeFilter;

        // Case-insensitive comparison for request type
        const requestType = (request.requestType || '').trim();
        const matchesRequestType = effectiveFilter === 'All' ||
            requestType.toLowerCase() === effectiveFilter.toLowerCase();

        // Apply date filter
        let matchesDate = true;

        // Check date range filters (dateFromFilter and dateToFilter) - filter by created date
        if (dateFromFilter || dateToFilter) {
            if (!request.createdAt) {
                matchesDate = false;
            } else {
                // Convert filter dates to Date objects
                const filterFrom = dateFromFilter ? new Date(dateFromFilter) : null;
                const filterTo = dateToFilter ? new Date(dateToFilter) : null;

                // Convert request created date to Date object
                let createdDate: Date;
                if (request.createdAt.toDate) {
                    createdDate = request.createdAt.toDate();
                } else if (request.createdAt.seconds) {
                    createdDate = new Date(request.createdAt.seconds * 1000);
                } else {
                    createdDate = new Date(request.createdAt);
                }

                // Set to start of day for comparison
                const createdDateOnly = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());

                if (filterFrom && filterTo) {
                    // Both filter dates provided - check if created date is within range
                    const filterFromDate = new Date(filterFrom.getFullYear(), filterFrom.getMonth(), filterFrom.getDate());
                    const filterToDate = new Date(filterTo.getFullYear(), filterTo.getMonth(), filterTo.getDate(), 23, 59, 59);
                    matchesDate = createdDateOnly >= filterFromDate && createdDateOnly <= filterToDate;
                } else if (filterFrom) {
                    // Only from date provided - check if created date is on or after filter date
                    const filterFromDate = new Date(filterFrom.getFullYear(), filterFrom.getMonth(), filterFrom.getDate());
                    matchesDate = createdDateOnly >= filterFromDate;
                } else if (filterTo) {
                    // Only to date provided - check if created date is on or before filter date
                    const filterToDate = new Date(filterTo.getFullYear(), filterTo.getMonth(), filterTo.getDate(), 23, 59, 59);
                    matchesDate = createdDateOnly <= filterToDate;
                }
            }
        } else if (dateFilter !== 'all') {
            // Use period filter (daily, weekly, monthly) if date range filters are not set
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            if (!request.createdAt) {
                matchesDate = false;
            } else {
                let requestDate: Date;
                if (request.createdAt.toDate) {
                    requestDate = request.createdAt.toDate();
                } else if (request.createdAt.seconds) {
                    requestDate = new Date(request.createdAt.seconds * 1000);
                } else {
                    requestDate = new Date(request.createdAt);
                }

                const requestDateOnly = new Date(requestDate.getFullYear(), requestDate.getMonth(), requestDate.getDate());

                switch (dateFilter) {
                    case 'daily':
                        matchesDate = requestDateOnly.getTime() === today.getTime();
                        break;
                    case 'weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        matchesDate = requestDateOnly.getTime() >= weekAgo.getTime();
                        break;
                    case 'monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        matchesDate = requestDateOnly.getTime() >= monthAgo.getTime();
                        break;
                    default:
                        matchesDate = true;
                }
            }
        }

        return matchesSearch && matchesStatus && matchesRequestType && matchesDate;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus, requestTypeFilter, dateFilter, dateFromFilter, dateToFilter, itemsPerPage]);

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm('');
        setDateFilter('all');
        setDateFromFilter('');
        setDateToFilter('');
        // Reset status filter based on user role
        if (psoDesignation === 'Regional PSO') {
            setFilterStatus('pending');
        } else {
            setFilterStatus('All');
        }
        setRequestTypeFilter('All');
        setCurrentPage(1);
    };

    // Fetch location details by locationId
    const fetchLocationDetails = async (locationId: string): Promise<any | null> => {
        // Check cache first
        if (locationDetailsCache[locationId]) {
            return locationDetailsCache[locationId];
        }

        try {
            const locationDoc = await getDoc(doc(db, 'locations', locationId));
            if (locationDoc.exists()) {
                const locationData = { id: locationDoc.id, ...locationDoc.data() };
                // Cache the location data
                setLocationDetailsCache(prev => ({ ...prev, [locationId]: locationData }));
                return locationData;
            }
        } catch (error) {
            console.error('Error fetching location details:', error);
        }
        return null;
    };

    // Fetch branch details by branchId
    const fetchBranchDetails = async (branchId: string, bankId: string): Promise<any | null> => {
        // Check cache first
        if (branchDetailsCache[branchId]) {
            return branchDetailsCache[branchId];
        }

        try {
            const branchDoc = await getDoc(doc(db, 'banks', bankId, 'branch', branchId));
            if (branchDoc.exists()) {
                const branchData: any = { id: branchDoc.id, ...branchDoc.data() };

                // If branch has locationId, fetch location details
                if (branchData.locationId) {
                    const locationData = await fetchLocationDetails(branchData.locationId);
                    if (locationData) {
                        // Store location name in branch data for easy access
                        branchData.locationName = locationData.name;
                    }
                }

                // Cache the branch data (with locationName if fetched)
                setBranchDetailsCache(prev => ({ ...prev, [branchId]: branchData }));

                return branchData;
            }
        } catch (error) {
            console.error('Error fetching branch details:', error);
        }
        return null;
    };

    // Get branch name from branches list or fetch if needed
    const getBranchName = async (request: Request): Promise<string> => {
        // First check if branchName is already in the request and is valid
        if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
            return request.branchName;
        }

        // Try to find branch from branches list by branchCode
        if (request.branchCode) {
            const branch = branches.find(b => b.branchCode === request.branchCode);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Try to find by branchId from branches list
        if (request.branchId) {
            const branch = branches.find(b => b.id === request.branchId);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // If branchId exists and we have companyId, try to fetch branch details
        if (request.branchId && companyId) {
            const branchData = await fetchBranchDetails(request.branchId, companyId);
            if (branchData) {
                return branchData.address || branchData.branchName || branchData.name || 'N/A';
            }
        }

        return 'N/A';
    };

    // Synchronous version for immediate display (will be updated when async fetch completes)
    const getBranchNameSync = (request: Request): string => {
        // First check if branchName is already in the request and is valid
        if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
            return request.branchName;
        }

        // Try to find branch from branches list by branchCode
        if (request.branchCode) {
            const branch = branches.find(b => b.branchCode === request.branchCode);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Try to find by branchId from branches list
        if (request.branchId) {
            const branch = branches.find(b => b.id === request.branchId);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Check cache
        if (request.branchId && branchDetailsCache[request.branchId]) {
            const branchData = branchDetailsCache[request.branchId];
            return branchData.address || branchData.branchName || branchData.name || 'N/A';
        }

        return 'N/A';
    };

    const resetApproveEmailStep = () => {
        setShowApproveEmailStep(false);
        setPendingApproveRequest(null);
        setApproveRecipients([]);
        setApproveEmailDraft('');
        setApproveLoadingRecipients(false);
    };

    const resetRejectEmailStep = () => {
        setShowRejectEmailStep(false);
        setPendingRejectRequest(null);
        setRejectRecipients([]);
        setRejectEmailDraft('');
        setRejectLoadingRecipients(false);
    };

    const closeRequestDetailsModal = () => {
        resetApproveEmailStep();
        resetRejectEmailStep();
        setShowDetailsModal(false);
        setSelectedRequest(null);
    };

    const inEmailRecipientStep = showApproveEmailStep || showRejectEmailStep;

    // Open details modal
    const handleViewDetails = async (request: Request) => {
        resetApproveEmailStep();
        resetRejectEmailStep();
        setSelectedRequest(request);
        setShowDetailsModal(true);

        // Get initial branch name synchronously
        const initialBranchName = getBranchNameSync(request);
        setDisplayBranchName(initialBranchName);
        setDisplayRegionName('N/A');

        // Use bankId from request if available, otherwise use companyId
        const bankId = (request as any).bankId || companyId;

        // Try to get branch data and location
        let branchData: any = null;

        if (request.branchId) {
            try {
                // Check if branch is already in our list
                const existingBranch = branches.find(b => b.id === request.branchId);
                if (existingBranch) {
                    branchData = existingBranch;
                } else if (branchDetailsCache[request.branchId]) {
                    // Use cached branch data
                    branchData = branchDetailsCache[request.branchId];
                } else if (bankId) {
                    // Fetch branch details
                    branchData = await fetchBranchDetails(request.branchId, bankId);
                }

                // Update branch name if we have branch data
                if (branchData) {
                    const branchName = branchData.address || branchData.branchName || branchData.name || 'N/A';
                    setDisplayBranchName(branchName);

                    // Get location/region name
                    if (branchData.locationName) {
                        // Location name already in branch data
                        setDisplayRegionName(branchData.locationName);
                    } else if (branchData.locationId) {
                        // Fetch location details
                        const locationData = await fetchLocationDetails(branchData.locationId);
                        if (locationData && locationData.name) {
                            setDisplayRegionName(locationData.name);
                            // Update branch data cache with location name
                            if (branchDetailsCache[request.branchId]) {
                                branchDetailsCache[request.branchId].locationName = locationData.name;
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching branch/location data:', error);
            }
        }
    };

    // Update request status
    const handleUpdateStatus = async (request: Request, newStatus: string) => {
        if (!companyId || !uid || !psoDesignation) return;

        setUpdating(true);
        try {
            await updateRequestStatus(companyId, request.id, newStatus, uid);

            setToastMessage(`Request status updated to ${newStatus}!`);
            setToastType('success');
            setShowToast(true);

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, psoDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);

            closeRequestDetailsModal();
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to update request status');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUpdating(false);
        }
    };

    const beginApproveEmailFlow = async (request: Request) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }
        resetRejectEmailStep();
        setPendingApproveRequest(request);
        setApproveEmailDraft('');
        setShowApproveEmailStep(true);
        setApproveLoadingRecipients(true);
        try {
            const unique = await getDefaultRecipientsForRequest(request, companyId);
            setApproveRecipients(unique);
        } catch (e) {
            console.error(e);
            setApproveRecipients([]);
            setToastMessage('Could not load suggested recipients');
            setToastType('warning');
            setShowToast(true);
        } finally {
            setApproveLoadingRecipients(false);
        }
    };

    const beginRejectEmailFlow = async (request: Request) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }
        resetApproveEmailStep();
        setPendingRejectRequest(request);
        setRejectEmailDraft('');
        setShowRejectEmailStep(true);
        setRejectLoadingRecipients(true);
        try {
            const unique = await getDefaultRecipientsForRequest(request, companyId);
            setRejectRecipients(unique);
        } catch (e) {
            console.error(e);
            setRejectRecipients([]);
            setToastMessage('Could not load suggested recipients');
            setToastType('warning');
            setShowToast(true);
        } finally {
            setRejectLoadingRecipients(false);
        }
    };

    const addApproveEmailRecipient = () => {
        const trimmed = approveEmailDraft.trim();
        if (!trimmed) return;
        const v = validateEmails(trimmed);
        if (v.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${v.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }
        setApproveRecipients((prev) => {
            const next = [...prev];
            for (const email of v.valid) {
                if (!next.some((e) => e.toLowerCase() === email.toLowerCase())) {
                    next.push(email);
                }
            }
            return next;
        });
        setApproveEmailDraft('');
    };

    const removeApproveEmailRecipient = (email: string) => {
        setApproveRecipients((prev) => prev.filter((e) => e !== email));
    };

    const addRejectEmailRecipient = () => {
        const trimmed = rejectEmailDraft.trim();
        if (!trimmed) return;
        const v = validateEmails(trimmed);
        if (v.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${v.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }
        setRejectRecipients((prev) => {
            const next = [...prev];
            for (const email of v.valid) {
                if (!next.some((e) => e.toLowerCase() === email.toLowerCase())) {
                    next.push(email);
                }
            }
            return next;
        });
        setRejectEmailDraft('');
    };

    const removeRejectEmailRecipient = (email: string) => {
        setRejectRecipients((prev) => prev.filter((e) => e !== email));
    };

    const closeStatusEmailModal = () => {
        setShowStatusEmailModal(false);
        setStatusEmailRequest(null);
        setStatusEmailRecipients([]);
        setStatusEmailDraft('');
        setStatusEmailLoadingDefaults(false);
    };

    const openStatusEmailModal = async (request: Request) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }
        setStatusEmailRequest(request);
        setStatusEmailRecipients([]);
        setStatusEmailDraft('');
        setShowStatusEmailModal(true);
        setStatusEmailLoadingDefaults(true);
        try {
            const defaults = await getDefaultRecipientsForRequest(request, companyId);
            setStatusEmailRecipients(defaults);
        } catch (e) {
            console.error(e);
            setToastMessage('Could not load suggested recipients');
            setToastType('warning');
            setShowToast(true);
        } finally {
            setStatusEmailLoadingDefaults(false);
        }
    };

    const addStatusEmailRecipient = () => {
        const trimmed = statusEmailDraft.trim();
        if (!trimmed) return;
        const v = validateEmails(trimmed);
        if (v.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${v.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }
        if (v.valid.length === 0) {
            setToastMessage('Enter at least one valid email address');
            setToastType('error');
            setShowToast(true);
            return;
        }
        setStatusEmailRecipients((prev) => {
            const next = [...prev];
            for (const email of v.valid) {
                if (!next.some((e) => e.toLowerCase() === email.toLowerCase())) {
                    next.push(email);
                }
            }
            return next;
        });
        setStatusEmailDraft('');
    };

    const removeStatusEmailRecipient = (email: string) => {
        setStatusEmailRecipients((prev) => prev.filter((e) => e !== email));
    };

    const sendStatusNotificationEmail = async () => {
        if (!statusEmailRequest || !companyId) return;
        const st = (statusEmailRequest.status || '').toLowerCase();
        if (st !== 'approved' && st !== 'rejected') return;
        if (statusEmailRecipients.length === 0) {
            setToastMessage('Add at least one recipient');
            setToastType('warning');
            setShowToast(true);
            return;
        }
        const validation = validateEmails(statusEmailRecipients.join(', '));
        if (validation.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${validation.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }

        setStatusEmailSending(true);
        try {
            const rid = statusEmailRequest.requestId || statusEmailRequest.id;
            const html =
                st === 'approved'
                    ? buildRequestApprovalEmailHtml(statusEmailRequest)
                    : buildRequestRejectionEmailHtml(statusEmailRequest);
            const subject =
                st === 'approved' ? `Request Approval - Request ID: ${rid}` : `Request Rejection - Request ID: ${rid}`;
            const results = await sendEmailsWithErrorHandling({
                to: validation.valid,
                subject,
                body: html,
                isHtml: true,
                companyName: 'PSO Request System Support',
            });
            if (results.failed.length > 0 && results.successful.length === 0) {
                setToastMessage(`Email failed: ${results.failed.map((f) => f.email).join(', ')}`);
                setToastType('error');
                setShowToast(true);
                return;
            }
            if (results.failed.length > 0) {
                setToastMessage(
                    `Sent to ${results.successful.length}. Failed: ${results.failed.map((f) => f.email).join(', ')}`
                );
                setToastType('warning');
            } else {
                setToastMessage(`Email sent to ${results.successful.length} recipient(s).`);
                setToastType('success');
            }
            setShowToast(true);
            closeStatusEmailModal();
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to send email');
            setToastType('error');
            setShowToast(true);
        } finally {
            setStatusEmailSending(false);
        }
    };

    const cancelApproveEmailStep = () => {
        resetApproveEmailStep();
    };

    const cancelRejectEmailStep = () => {
        resetRejectEmailStep();
    };

    const handleRejectEmailDone = async () => {
        if (!companyId || !uid || !pendingRejectRequest || !psoDesignation) return;

        const validation = validateEmails(rejectRecipients.join(', '));
        if (validation.invalid.length > 0) {
            setToastMessage(`Invalid email address(es): ${validation.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }

        setRejectSending(true);
        let partialEmailFailure = '';
        try {
            if (validation.valid.length > 0) {
                const rejectedAt = new Date();
                const html = buildRequestRejectionEmailHtml(pendingRejectRequest, rejectedAt);
                const rid = pendingRejectRequest.requestId || pendingRejectRequest.id;
                const results = await sendEmailsWithErrorHandling({
                    to: validation.valid,
                    subject: `Request Rejection - Request ID: ${rid}`,
                    body: html,
                    isHtml: true,
                    companyName: 'PSO Request System Support',
                });
                if (results.failed.length > 0 && results.successful.length === 0) {
                    setToastMessage(
                        `Could not send email. Request was not rejected. Failed: ${results.failed.map((f) => f.email).join(', ')}`
                    );
                    setToastType('error');
                    setShowToast(true);
                    return;
                }
                if (results.failed.length > 0) {
                    partialEmailFailure = ` Some recipients failed: ${results.failed.map((f) => f.email).join(', ')}.`;
                }
            }

            await updateRequestStatus(companyId, pendingRejectRequest.id, 'rejected', uid);

            const requestsList = await fetchRequests(companyId, psoId || uid, psoDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(
                new Set(requestsList.map((r) => r.status).filter((status): status is string => Boolean(status)))
            );
            setStatuses(statusList);

            closeRequestDetailsModal();

            if (validation.valid.length === 0) {
                setToastMessage('Request rejected. No email was sent (no recipients).');
                setToastType('success');
            } else if (partialEmailFailure) {
                setToastMessage(`Request rejected.${partialEmailFailure}`);
                setToastType('warning');
            } else {
                setToastMessage('Request rejected and notification email sent.');
                setToastType('success');
            }
            setShowToast(true);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to reject request or send email');
            setToastType('error');
            setShowToast(true);
        } finally {
            setRejectSending(false);
        }
    };

    const handleApproveEmailDone = async () => {
        if (!companyId || !uid || !pendingApproveRequest || !psoDesignation) return;

        const validation = validateEmails(approveRecipients.join(', '));
        if (validation.invalid.length > 0) {
            setToastMessage(`Invalid email address(es): ${validation.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }

        setApproveSending(true);
        let partialEmailFailure = '';
        try {
            if (validation.valid.length > 0) {
                const approvalAt = new Date();
                const html = buildRequestApprovalEmailHtml(pendingApproveRequest, approvalAt);
                const rid = pendingApproveRequest.requestId || pendingApproveRequest.id;
                const results = await sendEmailsWithErrorHandling({
                    to: validation.valid,
                    subject: `Request Approval - Request ID: ${rid}`,
                    body: html,
                    isHtml: true,
                    companyName: 'PSO Request System Support',
                });
                if (results.failed.length > 0 && results.successful.length === 0) {
                    setToastMessage(
                        `Could not send email. Request was not approved. Failed: ${results.failed.map((f) => f.email).join(', ')}`
                    );
                    setToastType('error');
                    setShowToast(true);
                    return;
                }
                if (results.failed.length > 0) {
                    partialEmailFailure = ` Some recipients failed: ${results.failed.map((f) => f.email).join(', ')}.`;
                }
            }

            await updateRequestStatus(companyId, pendingApproveRequest.id, 'approved', uid);

            const requestsList = await fetchRequests(companyId, psoId || uid, psoDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(
                new Set(requestsList.map((r) => r.status).filter((status): status is string => Boolean(status)))
            );
            setStatuses(statusList);

            closeRequestDetailsModal();

            if (validation.valid.length === 0) {
                setToastMessage('Request approved. No email was sent (no recipients).');
                setToastType('success');
            } else if (partialEmailFailure) {
                setToastMessage(`Request approved.${partialEmailFailure}`);
                setToastType('warning');
            } else {
                setToastMessage('Request approved and notification email sent.');
                setToastType('success');
            }
            setShowToast(true);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to approve request or send email');
            setToastType('error');
            setShowToast(true);
        } finally {
            setApproveSending(false);
        }
    };

    // Forward request to Head PSO
    const handleForwardToHead = async (request: Request) => {
        if (!companyId || !uid || !psoDesignation) return;

        setUpdating(true);
        try {
            await forwardRequestToHead(companyId, request.id, uid);

            setToastMessage('Request forwarded to Head PSO!');
            setToastType('success');
            setShowToast(true);

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, psoDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);

            closeRequestDetailsModal();
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to forward request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUpdating(false);
        }
    };

    // Get status badge
    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        } else if (statusLower === 'approved') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'rejected') {
            bgColor = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
        } else if (statusLower === 'in_progress') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (statusLower === 'completed') {
            bgColor = 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
        } else if (statusLower === 'forwarded_to_head') {
            bgColor = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status === 'forwarded_to_head' ? 'Forwarded To Head' : status}
            </span>
        );
    };

    // Get request type badge
    const getRequestTypeBadge = (type?: string) => {
        if (!type) return null;

        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (type === 'CCTV') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (type === 'Guard') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (type === 'BAMS') {
            bgColor = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {type}
            </span>
        );
    };

    // Format date
    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        return new Date(date).toLocaleDateString();
    };

    // Format datetime
    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleString();
        }
        return new Date(date).toLocaleString();
    };

    // Generate Request ID
    const generateRequestId = () => {
        if (!psoName || psoName.length < 2) {
            const timestamp = Date.now().toString().slice(-4);
            return `REQ-XX-${timestamp}`;
        }
        const psoInitials = psoName.substring(0, 2).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `REQ-${psoInitials}-${timestamp}`;
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            requestType: '',
            branchCode: '',
            branchName: '',
            company: '',
            selectedCompanyId: '',
            requesterName: '',
            requesterContact: '',
            requiredBy: '',
            cctvDateFrom: '',
            cctvDateTo: '',
            timeFrom: '',
            timeTo: '',
            numberOfGuards: '',
            guardCompany: '',
            hardwareName: '',
            purpose: '',
            purposeOther: '',
            requestId: '',
        });
        setSelectedBranch(null);
        setBranchSearch('');
    };

    // Filtered branches for search
    const filteredBranches = branches.filter(
        (b) =>
            b.branchCode?.toLowerCase().includes(branchSearch.toLowerCase()) ||
            b.address?.toLowerCase().includes(branchSearch.toLowerCase())
    );

    // Fetch available companies based on request type
    const loadAvailableCompanies = async () => {
        if (!companyId || !formData.requestType) return;

        try {
            const companiesList = await fetchAvailableCompanies(companyId, formData.requestType);
            setAvailableCompanies(companiesList);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    // Handle branch selection
    const handleBranchSelection = (branch: any) => {
        setSelectedBranch(branch);
        setFormData({
            ...formData,
            branchCode: branch.branchCode,
            branchName: branch.branchName || branch.address,
        });
        setShowBranchModal(false);
    };

    // Handle company selection
    const handleCompanySelection = (company: any) => {
        setFormData({
            ...formData,
            company: company.companyName,
            selectedCompanyId: company.companyId,
        });
        setShowCompanyModal(false);
    };

    // Handle update guard rate
    const handleUpdateGuardRate = async () => {
        if (!uid || !newRate) return;

        setSettingRate(true);
        try {
            const rate = parseFloat(newRate);
            if (isNaN(rate) || rate < 0) {
                setToastMessage('Please enter a valid rate');
                setToastType('error');
                setShowToast(true);
                return;
            }

            await updateGuardRate(rate, uid);
            setGuardRate(rate);
            setShowRateModal(false);
            setToastMessage('Guard daily rate updated successfully');
            setToastType('success');
            setShowToast(true);
        } catch (error) {
            console.error('Error updating rate:', error);
            setToastMessage('Failed to update rate');
            setToastType('error');
            setShowToast(true);
        } finally {
            setSettingRate(false);
        }
    };

    // Handle save request
    const handleSaveRequest = async () => {
        if (!uid || !selectedBranch) {
            setToastMessage('Please select a branch');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.cctvDateFrom || !formData.cctvDateTo) {
            setToastMessage('Please fill in all required fields');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Requester Name and Contact validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && (!formData.requesterName.trim() || !formData.requesterContact.trim())) {
            setToastMessage('Please fill in requester name and contact');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Required By validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && !formData.requiredBy) {
            setToastMessage('Please select Required By');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Time validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && (!formData.timeFrom || !formData.timeTo)) {
            setToastMessage('Please fill in time range');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (formData.requestType === 'Guard' && !formData.numberOfGuards) {
            setToastMessage('Please fill in number of guards');
            setToastType('error');
            setShowToast(true);
            return;
        }


        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setSaving(true);
        try {
            const newRequestId = generateRequestId();

            const requestData: any = {
                requestId: newRequestId,
                branchCode: formData.branchCode,
                branchName: formData.branchName,
                company: formData.company,
                companyId: formData.selectedCompanyId,
                requesterName: formData.requesterName.trim(),
                requesterContact: formData.requesterContact.trim(),
                requiredBy: formData.requiredBy,
                requestType: formData.requestType,
                cctvDateFrom: formData.cctvDateFrom,
                cctvDateTo: formData.cctvDateTo,
                timeFrom: formData.timeFrom,
                timeTo: formData.timeTo,
                purpose: formData.purpose,
                purposeOther: formData.purpose === 'Other' ? formData.purposeOther.trim() : '',
                branchId: selectedBranch.id,
                createdBy: psoId || uid,
                status: 'pending',
                createdAt: new Date(),
                hardwareName: formData.hardwareName,
            };

            // Add type-specific fields
            if (formData.requestType === 'Guard') {
                requestData.numberOfGuards = formData.numberOfGuards;
                requestData.guardCompany = formData.guardCompany;
            }

            await createRequest(companyId, requestData);

            setToastMessage(`${formData.requestType} Request submitted successfully! Request ID: ${newRequestId}`);
            setToastType('success');
            setShowToast(true);

            // Reset form and close modal
            resetForm();
            setShowAddModal(false);

            // Refresh requests list
            if (companyId && psoId && psoDesignation) {
                const requestsList = await fetchRequests(companyId, psoId, psoDesignation, managedPsoIds);
                setRequests(requestsList);
                const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
                setStatuses(statusList);
            }
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to submit request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setSaving(false);
        }
    };

    // Export to Excel
    const exportToExcel = async () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        // Helper function to get branch data for a request
        const getBranchData = async (request: Request): Promise<any> => {
            let branchData: any = null;
            const bankId = (request as any).bankId || companyId;

            if (request.branchId) {
                // Check if branch is in our list
                const existingBranch = branches.find(b => b.id === request.branchId);
                if (existingBranch) {
                    branchData = existingBranch;
                } else if (branchDetailsCache[request.branchId]) {
                    branchData = branchDetailsCache[request.branchId];
                } else if (bankId) {
                    branchData = await fetchBranchDetails(request.branchId, bankId);
                }
            }
            return branchData;
        };

        // Helper function to get branch name for a request
        const getBranchName = async (request: Request): Promise<string> => {
            // First check if branchName is already in the request and is valid
            if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
                return request.branchName;
            }

            const branchData = await getBranchData(request);
            if (branchData) {
                return branchData.address || branchData.branchName || branchData.name || 'N/A';
            }

            return 'N/A';
        };

        // Helper function to get region name for a request
        const getRegionName = async (request: Request): Promise<string> => {
            const branchData = await getBranchData(request);

            if (branchData) {
                if (branchData.locationName) {
                    return branchData.locationName;
                } else if (branchData.locationId) {
                    const locationData = await fetchLocationDetails(branchData.locationId);
                    if (locationData && locationData.name) {
                        return locationData.name;
                    }
                }
            }
            return 'N/A';
        };

        // Fetch branch and region data for all requests
        const worksheetDataPromises = filteredRequests.map(async (request) => {
            const [branchName, regionName] = await Promise.all([
                getBranchName(request),
                getRegionName(request)
            ]);

            // Get time fields - check for cctvTimeFrom/cctvTimeTo first
            const timeFrom = (request as any).cctvTimeFrom && (request as any).cctvTimeFrom.trim() !== ''
                ? (request as any).cctvTimeFrom
                : (request.timeFrom && request.timeFrom.trim() !== '' ? request.timeFrom : 'N/A');
            const timeTo = (request as any).cctvTimeTo && (request as any).cctvTimeTo.trim() !== ''
                ? (request as any).cctvTimeTo
                : (request.timeTo && request.timeTo.trim() !== '' ? request.timeTo : 'N/A');

            return {
                'Request ID': request.requestId || 'N/A',
                'Request Type': request.requestType || 'N/A',
                'Branch Code': request.branchCode || 'N/A',
                'Branch Name': branchName,
                'Region': regionName,
                'Company': request.company || 'N/A',
                'Requester Name': request.requesterName || 'N/A',
                'Requester Contact': request.requesterContact || 'N/A',
                'Required By': request.requiredBy || 'N/A',
                'Date From': formatDate(getDateFields(request).dateFrom),
                'Date To': formatDate(getDateFields(request).dateTo),
                'Time From': timeFrom,
                'Time To': timeTo,
                'Number of Guards': request.numberOfGuards || 'N/A',
                'Hardware Name': request.hardwareName || 'N/A',
                'Purpose': (request as any).purposeOfFootage || request.purpose || 'N/A',
                'Status': request.status || 'N/A',
                'Created At': formatDateTime(request.createdAt),
                'Updated At': request.updatedAt ? formatDateTime(request.updatedAt) : 'N/A',
            };
        });

        const worksheetData = await Promise.all(worksheetDataPromises);

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Requests');

        const fileName = `Request_Reports_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Export to PDF
    const exportToPDF = () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const doc = new jsPDF();
        let yPosition = 20;

        // Professional Header
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('ADVANCE GUARD SECURITY SERVICES', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(16);
        doc.setTextColor(52, 73, 94);
        doc.text('REQUEST REPORTS', 20, yPosition);
        yPosition += 15;

        // Report Information
        doc.setFillColor(248, 249, 250);
        doc.rect(15, yPosition - 5, 180, 35, 'F');
        doc.setDrawColor(189, 195, 199);
        doc.rect(15, yPosition - 5, 180, 35, 'S');

        doc.setFontSize(10);
        doc.setTextColor(52, 73, 94);
        doc.setFont('helvetica', 'bold');
        doc.text('REPORT INFORMATION', 20, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${dateFilter === 'all' ? 'All Time' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Total Requests: ${filteredRequests.length}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPosition);
        yPosition += 15;

        // Prepare table data
        const tableData = filteredRequests.map(request => {
            const dates = getDateFields(request);
            return [
                request.requestId || 'N/A',
                request.requestType || 'N/A',
                request.branchCode || 'N/A',
                request.requesterName || 'N/A',
                formatDate(dates.dateFrom),
                formatDate(dates.dateTo),
                request.status || 'N/A',
                formatDate(request.createdAt),
            ];
        });

        // Add table using autoTable
        autoTable(doc, {
            head: [['Request ID', 'Type', 'Branch Code', 'Requester', 'Date From', 'Date To', 'Status', 'Created']],
            body: tableData,
            startY: yPosition,
            styles: {
                fontSize: 7,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontStyle: 'bold',
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250],
            },
            margin: { top: yPosition, left: 15, right: 15 },
        });

        const fileName = `Request_Reports_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        setToastMessage('PDF file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    const columns: Column<Request>[] = [
        {
            header: 'Request ID',
            accessor: 'requestId',
            className: 'whitespace-nowrap font-medium text-gray-900 dark:text-gray-100',
        },
        {
            header: 'Type',
            render: (request: Request) => getRequestTypeBadge(request.requestType),
        },
        {
            header: 'Branch Code',
            accessor: 'branchCode',
        },
        {
            header: 'Requester',
            accessor: 'requesterName',
        },
        {
            header: 'Required By',
            accessor: 'requiredBy',
        },
        {
            header: 'Date From',
            render: (request: Request) => formatDate(getDateFields(request).dateFrom),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Date To',
            render: (request: Request) => formatDate(getDateFields(request).dateTo),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Status',
            render: (request: Request) => getStatusBadge(request.status),
        },
        {
            header: 'Total Amount',
            render: (request: Request) => {
                if (request.totalAmount) {
                    return `PKR ${request.totalAmount.toLocaleString()}`;
                }
                // Fallback for old requests or if not stored (though we plan to store it)
                if (request.requestType === 'Guard' && request.numberOfGuards && request.guardDailyRate) {
                    // Start calculate if we have rate but no total (shouldn't happen with new logic but safe fallback)
                    // We need days... let's just show N/A if not stored to avoid complexity in column render
                    return 'N/A';
                }
                if (request.requestType === 'Guard') {
                    return 'N/A'; // Or calculate if we really want to, but stored is better
                }
                return 'N/A';
            },
            className: 'text-gray-600 dark:text-gray-400 font-medium',
        },
        {
            header: 'Created',
            render: (request: Request) => formatDate(request.createdAt),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Action',
            render: (request: Request) => {
                const fs = filterStatus.toLowerCase();
                const rs = (request.status || '').toLowerCase();
                const showStatusEmail =
                    (fs === 'approved' && rs === 'approved') || (fs === 'rejected' && rs === 'rejected');
                return (
                    <div className="flex justify-end gap-2">
                        {showStatusEmail && (
                            <button
                                type="button"
                                title="Send status notification email"
                                aria-label="Send status notification email"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void openStatusEmailModal(request);
                                }}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-400 dark:hover:bg-blue-900/20"
                            >
                                <Icon icon={FiMail} className="h-4 w-4" />
                            </button>
                        )}
                        <button
                            type="button"
                            title="View details"
                            aria-label="View details"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(request);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-400 dark:hover:bg-blue-900/20"
                        >
                            <Icon icon={FiEye} className="h-4 w-4" />
                        </button>
                    </div>
                );
            },
            align: 'right',
        },
    ];

    const headerActions = (
        <div className="flex gap-3">
            {canCreateRequests && (
                <Button variant="primary" onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                    <Icon icon={FiPlus} className="h-4 w-4" />
                    Add Request Form
                </Button>
            )}
            {/* Admin/Incharge Settings Button */}
            {(designation === 'Admin' || isIncharge || isBankUser) && (
                <Button variant="secondary" onClick={() => setShowRateModal(true)} className="flex items-center gap-2">
                    <Icon icon={FiRotateCcw} className="h-4 w-4" /> {/* Reusing icon or pick a better one like FiSettings if imported */}
                    Set Guard Rate
                </Button>
            )}
            {!canCreateRequests && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                    <Icon icon={FiAlertCircle} className="h-4 w-4" />
                    View Only - Only Incharge MIS can create requests
                </div>
            )}
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiFile} className="h-4 w-4" />
                Export Excel
            </Button>
            <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export PDF
            </Button>
        </div>
    );

    const filters = (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters</h3>
                <Button
                    variant="secondary"
                    onClick={resetFilters}
                    className="flex items-center gap-2"
                >
                    <Icon icon={FiRotateCcw} className="h-4 w-4" />
                    Reset Filters
                </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Search Requests
                    </label>
                    <TextField
                        type="text"
                        placeholder="Search by request ID, branch code, or requester..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        prefix={<Icon icon={FiSearch} />}
                    />
                </div>
                {/* Request Type Filter - Show for Incharge MIS and PSO users */}
                {(!isIncharge || isInchargeMIS) && (
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Filter by Request Type
                        </label>
                        <select
                            value={requestTypeFilter}
                            onChange={(e) => setRequestTypeFilter(e.target.value as 'All' | 'CCTV' | 'Guard' | 'BAMS')}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                        >
                            <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Types</option>
                            <option value="CCTV" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">CCTV</option>
                            <option value="Guard" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Guard</option>
                            <option value="BAMS" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">BAMS</option>
                        </select>
                    </div>
                )}
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Filter by Date Period
                    </label>
                    <select
                        value={dateFilter}
                        onChange={(e) => {
                            setDateFilter(e.target.value as 'all' | 'daily' | 'weekly' | 'monthly');
                            // Clear date range filters when period filter is used
                            if (e.target.value !== 'all') {
                                setDateFromFilter('');
                                setDateToFilter('');
                            }
                        }}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    >
                        <option value="all" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Time</option>
                        <option value="daily" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Daily</option>
                        <option value="weekly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Weekly</option>
                        <option value="monthly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Monthly</option>
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Date From
                    </label>
                    <input
                        type="date"
                        value={dateFromFilter}
                        onChange={(e) => {
                            setDateFromFilter(e.target.value);
                            // Clear period filter when date range is used
                            if (e.target.value || dateToFilter) {
                                setDateFilter('all');
                            }
                        }}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Date To
                    </label>
                    <input
                        type="date"
                        value={dateToFilter}
                        onChange={(e) => {
                            setDateToFilter(e.target.value);
                            // Clear period filter when date range is used
                            if (dateFromFilter || e.target.value) {
                                setDateFilter('all');
                            }
                        }}
                        min={dateFromFilter || undefined}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Filter by Status
                    </label>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    >
                        <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Statuses</option>
                        {statuses.map((status) => (
                            <option key={status} value={status} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{status}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="p-6">
                <TableComp
                    title="View Requests"
                    description="Manage and track request forms"
                    data={paginatedRequests}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage="No requests found matching your criteria."
                    onRowClick={handleViewDetails}
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage,
                    }}
                />
            </div>

            {/* Request Details Modal */}
            {showDetailsModal && selectedRequest && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={closeRequestDetailsModal}
                    title={`Request Details - ${selectedRequest.requestId}`}
                    size="lg"
                    closeOnBackdropClick={false}
                >
                    <div className="p-6">
                        {(() => {
                            const fs = filterStatus.toLowerCase();
                            const rs = (selectedRequest.status || '').toLowerCase();
                            const rowIsTerminal = rs === 'approved' || rs === 'rejected';
                            const filterMatchesRow =
                                (fs === 'approved' && rs === 'approved') || (fs === 'rejected' && rs === 'rejected');
                            const showStatusEmailInDetails =
                                !inEmailRecipientStep && rowIsTerminal && (filterMatchesRow || fs === 'all');
                            if (!showStatusEmailInDetails) return null;
                            return (
                                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/90 bg-slate-50/90 px-4 py-3 dark:border-gray-600 dark:bg-gray-900/50">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Resend the {rs === 'approved' ? 'approval' : 'rejection'} notification by email.
                                    </p>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        className="!py-2 !px-3 text-xs"
                                        onClick={() => void openStatusEmailModal(selectedRequest)}
                                    >
                                        <Icon icon={FiMail} className="mr-1.5 h-3.5 w-3.5" />
                                        Send email
                                    </Button>
                                </div>
                            );
                        })()}
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request ID</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requestId || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Type</label>
                                    <p className="mt-1">{getRequestTypeBadge(selectedRequest.requestType)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchCode || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{displayBranchName}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Company</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.company || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Region</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{displayRegionName}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterName || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Contact</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterContact || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Required By</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requiredBy || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                    <p className="mt-1">{getStatusBadge(selectedRequest.status)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date From</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(getDateFields(selectedRequest).dateFrom)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date To</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(getDateFields(selectedRequest).dateTo)}</p>
                                </div>
                                {selectedRequest.requestType !== 'Guard' && (
                                    <>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Time From</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {(() => {
                                                    const cctvTimeFrom = (selectedRequest as any).cctvTimeFrom;
                                                    const timeFrom = selectedRequest.timeFrom;
                                                    if (cctvTimeFrom && cctvTimeFrom.trim() !== '') {
                                                        return cctvTimeFrom;
                                                    }
                                                    if (timeFrom && timeFrom.trim() !== '') {
                                                        return timeFrom;
                                                    }
                                                    return 'N/A';
                                                })()}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Time To</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {(() => {
                                                    const cctvTimeTo = (selectedRequest as any).cctvTimeTo;
                                                    const timeTo = selectedRequest.timeTo;
                                                    if (cctvTimeTo && cctvTimeTo.trim() !== '') {
                                                        return cctvTimeTo;
                                                    }
                                                    if (timeTo && timeTo.trim() !== '') {
                                                        return timeTo;
                                                    }
                                                    return 'N/A';
                                                })()}
                                            </p>
                                        </div>
                                    </>
                                )}
                                {selectedRequest.requestType === 'Guard' && (
                                    <>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Number of Guards</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.numberOfGuards || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Daily Rate Used</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {selectedRequest.guardDailyRate ? `PKR ${selectedRequest.guardDailyRate.toLocaleString()}` : 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Total Amount</label>
                                            <p className="mt-1 text-sm font-bold text-green-600 dark:text-green-400">
                                                {selectedRequest.totalAmount ? `PKR ${selectedRequest.totalAmount.toLocaleString()}` : 'N/A'}
                                            </p>
                                        </div>
                                    </>
                                )}
                                {selectedRequest.requestType === 'BAMS' && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Hardware Name</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.hardwareName || 'N/A'}</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Purpose</label>
                                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {(selectedRequest as any).purposeOfFootage || selectedRequest.purpose || 'No purpose specified'}
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.createdAt)}</p>
                                </div>
                                {selectedRequest.updatedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Updated At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.updatedAt)}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer Actions */}
                        <div className="mt-6 flex flex-col gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                            {showApproveEmailStep && pendingApproveRequest && (
                                <div className="rounded-xl border border-blue-200/90 bg-gradient-to-b from-blue-50/95 via-white to-white p-4 shadow-sm dark:border-blue-900/45 dark:from-blue-950/35 dark:via-gray-950 dark:to-gray-950">
                                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                Approval notification
                                            </h3>
                                            <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                                                Recipients below receive the approval email. Add or remove addresses, then click{' '}
                                                <strong className="text-gray-800 dark:text-gray-200">Done</strong> to send and approve
                                                this request.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Recipients
                                    </label>
                                    {approveLoadingRecipients ? (
                                        <div className="flex min-h-[5.5rem] items-center justify-center rounded-xl border border-dashed border-blue-200/80 bg-blue-50/50 text-sm text-gray-500 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-gray-400">
                                            Loading suggested recipients…
                                        </div>
                                    ) : (
                                        <div className="min-h-[5.5rem] rounded-xl border border-gray-200/90 bg-gradient-to-b from-slate-50/90 to-white p-3 shadow-inner dark:border-gray-600 dark:from-gray-900/70 dark:to-gray-900/45">
                                            {approveRecipients.length === 0 ? (
                                                <p className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                                    No recipients yet — add at least one address, or continue with none to approve without
                                                    email.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {approveRecipients.map((email) => (
                                                        <span key={email} className={recipientChipClass}>
                                                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/45 dark:text-blue-300">
                                                                <Icon icon={FiMail} className="h-3.5 w-3.5" />
                                                            </span>
                                                            <span className={recipientChipTextClass} title={email}>
                                                                {email}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeApproveEmailRecipient(email)}
                                                                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/35 dark:hover:text-red-300"
                                                                aria-label={`Remove ${email}`}
                                                            >
                                                                <Icon icon={FiX} className="h-3.5 w-3.5" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-3">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Add recipient
                                        </label>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                                            <TextField
                                                type="email"
                                                className="min-w-0 flex-1"
                                                placeholder="email@example.com (commas for several)"
                                                value={approveEmailDraft}
                                                onChange={(e) => setApproveEmailDraft(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addApproveEmailRecipient();
                                                    }
                                                }}
                                                disabled={approveLoadingRecipients}
                                            />
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="shrink-0 sm:self-start"
                                                onClick={addApproveEmailRecipient}
                                                disabled={approveLoadingRecipients}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {showRejectEmailStep && pendingRejectRequest && (
                                <div className="rounded-xl border border-red-200/90 bg-gradient-to-b from-red-50/90 via-white to-white p-4 shadow-sm dark:border-red-900/40 dark:from-red-950/30 dark:via-gray-950 dark:to-gray-950">
                                    <div className="mb-3">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            Rejection notification
                                        </h3>
                                        <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                                            Recipients below receive the rejection email. Add or remove addresses, then confirm to send and
                                            mark this request as rejected.
                                        </p>
                                    </div>
                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Recipients
                                    </label>
                                    {rejectLoadingRecipients ? (
                                        <div className="flex min-h-[5.5rem] items-center justify-center rounded-xl border border-dashed border-red-200/80 bg-red-50/40 text-sm text-gray-500 dark:border-red-900/45 dark:bg-red-950/20 dark:text-gray-400">
                                            Loading suggested recipients…
                                        </div>
                                    ) : (
                                        <div className="min-h-[5.5rem] rounded-xl border border-gray-200/90 bg-gradient-to-b from-slate-50/90 to-white p-3 shadow-inner dark:border-gray-600 dark:from-gray-900/70 dark:to-gray-900/45">
                                            {rejectRecipients.length === 0 ? (
                                                <p className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                                    No recipients yet — add addresses to notify by email, or continue with none to reject
                                                    without email.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {rejectRecipients.map((email) => (
                                                        <span key={email} className={recipientChipClass}>
                                                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                                                <Icon icon={FiMail} className="h-3.5 w-3.5" />
                                                            </span>
                                                            <span className={recipientChipTextClass} title={email}>
                                                                {email}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeRejectEmailRecipient(email)}
                                                                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/35 dark:hover:text-red-300"
                                                                aria-label={`Remove ${email}`}
                                                            >
                                                                <Icon icon={FiX} className="h-3.5 w-3.5" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-3">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Add recipient
                                        </label>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                                            <TextField
                                                type="email"
                                                className="min-w-0 flex-1"
                                                placeholder="email@example.com (commas for several)"
                                                value={rejectEmailDraft}
                                                onChange={(e) => setRejectEmailDraft(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addRejectEmailRecipient();
                                                    }
                                                }}
                                                disabled={rejectLoadingRecipients}
                                            />
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="shrink-0 sm:self-start"
                                                onClick={addRejectEmailRecipient}
                                                disabled={rejectLoadingRecipients}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-wrap items-center justify-end gap-3">
                            <Button variant="secondary" onClick={closeRequestDetailsModal}>
                                Close
                            </Button>

                            {showApproveEmailStep && pendingApproveRequest && (
                                <>
                                    <Button variant="secondary" onClick={cancelApproveEmailStep} disabled={approveSending}>
                                        Back
                                    </Button>
                                    <Button variant="primary" onClick={() => void handleApproveEmailDone()} disabled={approveSending}>
                                        {approveSending ? (
                                            <>
                                                <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                Done
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}

                            {showRejectEmailStep && pendingRejectRequest && (
                                <>
                                    <Button variant="secondary" onClick={cancelRejectEmailStep} disabled={rejectSending}>
                                        Back
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={() => void handleRejectEmailDone()}
                                        disabled={rejectSending}
                                        className="!bg-red-600 hover:!bg-red-700"
                                    >
                                        {rejectSending ? (
                                            <>
                                                <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                                Confirm reject
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}

                            {/* Regular PSO: No action buttons */}
                            {!inEmailRecipientStep && psoDesignation === 'PSO' && (
                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <Icon icon={FiAlertCircle} className="mr-1 h-4 w-4" />
                                    View only - No actions available
                                </div>
                            )}

                            {/* Regional PSO: Approve/Reject buttons for pending requests */}
                            {!inEmailRecipientStep && psoDesignation === 'Regional PSO' && selectedRequest?.status === 'pending' && (
                                <>
                                    <Button
                                        variant="primary"
                                        onClick={() => void beginApproveEmailFlow(selectedRequest)}
                                        disabled={updating || approveSending || rejectSending}
                                    >
                                        <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                        Approve
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => void beginRejectEmailFlow(selectedRequest)}
                                        disabled={updating || approveSending || rejectSending}
                                        className="!bg-red-600 hover:!bg-red-700"
                                    >
                                        <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                        Reject
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleForwardToHead(selectedRequest)}
                                        disabled={updating}
                                    >
                                        {updating ? (
                                            <>
                                                <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                Forwarding...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon={FiClock} className="mr-1 h-4 w-4" />
                                                Forward to Head PSO
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}

                            {/* Regional PSO: Hide buttons if already forwarded */}
                            {!inEmailRecipientStep && psoDesignation === 'Regional PSO' && selectedRequest?.status === 'forwarded_to_head' && (
                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                    Request already forwarded to Head PSO
                                </div>
                            )}

                            {/* Regional PSO: Show status message for approved/rejected requests */}
                            {!inEmailRecipientStep && psoDesignation === 'Regional PSO' &&
                                selectedRequest?.status !== 'pending' &&
                                selectedRequest?.status !== 'forwarded_to_head' && (
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                        Request status: {selectedRequest?.status}
                                    </div>
                                )}

                            {/* Head PSO: Approve/Reject buttons */}
                            {!inEmailRecipientStep && psoDesignation === 'Head PSO' && (
                                <>
                                    {(selectedRequest?.status === 'pending' || selectedRequest?.status === 'forwarded_to_head') && (
                                        <>
                                            <Button
                                                variant="primary"
                                                onClick={() => void beginApproveEmailFlow(selectedRequest)}
                                                disabled={updating || approveSending || rejectSending}
                                            >
                                                <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                Approve
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={() => void beginRejectEmailFlow(selectedRequest)}
                                                disabled={updating || approveSending || rejectSending}
                                                className="!bg-red-600 hover:!bg-red-700"
                                            >
                                                <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                    {selectedRequest?.status === 'approved' && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleUpdateStatus(selectedRequest, 'in_progress')}
                                            disabled={updating}
                                        >
                                            {updating ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    Starting...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiClock} className="mr-1 h-4 w-4" />
                                                    Start Processing
                                                </>
                                            )}
                                        </Button>
                                    )}
                                    {selectedRequest?.status === 'in_progress' && (
                                        <Button
                                            variant="primary"
                                            onClick={() => handleUpdateStatus(selectedRequest, 'completed')}
                                            disabled={updating}
                                        >
                                            {updating ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    Completing...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                    Mark Complete
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </>
                            )}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {showStatusEmailModal && statusEmailRequest && (
                <Modal
                    isOpen={showStatusEmailModal}
                    onClose={closeStatusEmailModal}
                    title={`Send email — ${statusEmailRequest.requestId || statusEmailRequest.id}`}
                    size="lg"
                    closeOnBackdropClick={false}
                >
                    <div className="space-y-4 p-6">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Status:{' '}
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(statusEmailRequest.status || '').toLowerCase() === 'approved'
                                    ? 'Approved'
                                    : 'Rejected'}
                            </span>
                            . Add or remove recipients, then send the notification.
                        </p>

                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200">
                                Recipients
                            </label>
                            {statusEmailLoadingDefaults ? (
                                <div className="flex min-h-[5.5rem] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-400">
                                    Loading suggested recipients…
                                </div>
                            ) : (
                                <div className="min-h-[5.5rem] rounded-xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white p-3 shadow-inner dark:border-gray-600 dark:from-gray-900/70 dark:to-gray-900/40">
                                    {statusEmailRecipients.length === 0 ? (
                                        <p className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No recipients yet — add addresses below.
                                        </p>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {statusEmailRecipients.map((email) => (
                                                <span key={email} className={recipientChipClass}>
                                                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/45 dark:text-blue-300">
                                                        <Icon icon={FiMail} className="h-3.5 w-3.5" />
                                                    </span>
                                                    <span className={recipientChipTextClass} title={email}>
                                                        {email}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeStatusEmailRecipient(email)}
                                                        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/35 dark:hover:text-red-300"
                                                        aria-label={`Remove ${email}`}
                                                    >
                                                        <Icon icon={FiX} className="h-3.5 w-3.5" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Add recipient
                            </label>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                                <TextField
                                    type="email"
                                    className="min-w-0 flex-1"
                                    placeholder="email@example.com (use commas for several)"
                                    value={statusEmailDraft}
                                    onChange={(e) => setStatusEmailDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addStatusEmailRecipient();
                                        }
                                    }}
                                />
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="shrink-0 sm:self-start"
                                    onClick={addStatusEmailRecipient}
                                    disabled={statusEmailLoadingDefaults}
                                >
                                    Add
                                </Button>
                            </div>
                        </div>

                        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button variant="secondary" onClick={closeStatusEmailModal} disabled={statusEmailSending}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => void sendStatusNotificationEmail()}
                                disabled={statusEmailSending || statusEmailLoadingDefaults}
                            >
                                {statusEmailSending ? 'Sending…' : 'Send email'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Request Form Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        resetForm();
                    }}
                    title="Request Form"
                    size="xl"
                >
                    <div className="p-6">
                        {formData.requestId && (
                            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                    <strong>Request ID:</strong> {formData.requestId}
                                </p>
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* Request Type */}
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Request Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.requestType}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            requestType: e.target.value,
                                            company: '',
                                            selectedCompanyId: '',
                                        });
                                    }}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                >
                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Request Type</option>
                                    {requestTypeOptions.map((type) => (
                                        <option key={type} value={type} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{type}</option>
                                    ))}
                                </select>
                            </div>

                            {formData.requestType && (
                                <>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        {/* Branch Code */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch Code <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.branchCode}
                                                placeholder="Click to select branch"
                                                readOnly
                                                onClick={() => setShowBranchModal(true)}
                                                className="cursor-pointer"
                                            />
                                        </div>

                                        {/* Branch Name */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch Name
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.branchName}
                                                placeholder="Auto-filled from branch selection"
                                                readOnly
                                            />
                                        </div>

                                        {/* Required By - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Required By <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={formData.requiredBy}
                                                    onChange={(e) => setFormData({ ...formData, requiredBy: e.target.value })}
                                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                >
                                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Required By</option>
                                                    {requiredByOptions.map((option) => (
                                                        <option key={option} value={option} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{option}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Company */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Company <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.company}
                                                placeholder={`Click to select ${formData.requestType} company`}
                                                readOnly
                                                onClick={() => {
                                                    loadAvailableCompanies();
                                                    setShowCompanyModal(true);
                                                }}
                                                className="cursor-pointer"
                                            />
                                        </div>

                                        {/* Requester Name - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Requester Name <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="text"
                                                    value={formData.requesterName}
                                                    onChange={(e) => setFormData({ ...formData, requesterName: e.target.value })}
                                                    placeholder="Enter requester name"
                                                />
                                            </div>
                                        )}

                                        {/* Requester Contact - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Requester Contact <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="text"
                                                    value={formData.requesterContact}
                                                    onChange={(e) => setFormData({ ...formData, requesterContact: e.target.value })}
                                                    placeholder="Enter contact number"
                                                />
                                            </div>
                                        )}

                                        {/* Date From */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Date From <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="date"
                                                value={formData.cctvDateFrom}
                                                onChange={(e) => setFormData({ ...formData, cctvDateFrom: e.target.value })}
                                            />
                                        </div>

                                        {/* Date To */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Date To <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="date"
                                                value={formData.cctvDateTo}
                                                onChange={(e) => setFormData({ ...formData, cctvDateTo: e.target.value })}
                                            />
                                        </div>

                                        {/* Time From - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Time From <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="time"
                                                    value={formData.timeFrom}
                                                    onChange={(e) => setFormData({ ...formData, timeFrom: e.target.value })}
                                                />
                                            </div>
                                        )}

                                        {/* Time To - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Time To <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="time"
                                                    value={formData.timeTo}
                                                    onChange={(e) => setFormData({ ...formData, timeTo: e.target.value })}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Additional Fields based on Request Type */}
                                    {formData.requestType === 'Guard' && (
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Number of Guards <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.numberOfGuards}
                                                onChange={(e) => setFormData({ ...formData, numberOfGuards: e.target.value })}
                                                placeholder="Enter number of guards"
                                            />
                                        </div>
                                    )}

                                    {/* Estimated Total for Guard Requests */}
                                    {formData.requestType === 'Guard' && (
                                        <div className="col-span-1 md:col-span-2 rounded-lg bg-blue-50 p-4 border border-blue-100 dark:bg-blue-900/20 dark:border-blue-800">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">Daily Rate</p>
                                                    <p className="text-lg font-bold text-blue-900 dark:text-blue-100">PKR {guardRate.toLocaleString()}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">Estimated Total Amount</p>
                                                    <p className="text-xl font-bold text-green-600 dark:text-green-400">
                                                        {(() => {
                                                            const guards = Number(formData.numberOfGuards) || 0;
                                                            let days = 0;
                                                            // Use guardDate fields if we add them or reuse cctvDate fields as generic date fields
                                                            const dateFromStr = (formData as any).guardDateFrom || formData.cctvDateFrom;
                                                            const dateToStr = (formData as any).guardDateTo || formData.cctvDateTo;

                                                            if (dateFromStr && dateToStr) {
                                                                const d1 = new Date(dateFromStr);
                                                                const d2 = new Date(dateToStr);
                                                                const diff = Math.abs(d2.getTime() - d1.getTime());
                                                                days = Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
                                                            }

                                                            const total = guards * guardRate * days;
                                                            return total > 0 ? `PKR ${total.toLocaleString()}` : 'PKR 0';
                                                        })()}
                                                    </p>
                                                    <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                                                        (Rate x Guards x Days)
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}


                                    {/* Purpose */}
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Purpose <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.purpose}
                                            onChange={(e) => setFormData({ ...formData, purpose: e.target.value, purposeOther: '' })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                        >
                                            <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Purpose</option>
                                            {(formData.requestType === 'Guard' ? purposeOptionsGuard : purposeOptionsCCTV).map((purpose) => (
                                                <option key={purpose} value={purpose} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{purpose}</option>
                                            ))}
                                        </select>
                                        {formData.purpose === 'Other' && (
                                            <div className="mt-3">
                                                <TextField
                                                    type="text"
                                                    value={formData.purposeOther}
                                                    onChange={(e) => setFormData({ ...formData, purposeOther: e.target.value })}
                                                    placeholder={formData.requestType === 'Guard' ? 'Please specify the reason...' : 'Please specify the purpose...'}
                                                    className="mt-1"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                setShowAddModal(false);
                                                resetForm();
                                            }}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSaveRequest}
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    Submitting...
                                                </>
                                            ) : (
                                                'Submit Request'
                                            )}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Rate Setting Modal */}
            {showRateModal && (
                <Modal
                    isOpen={showRateModal}
                    onClose={() => setShowRateModal(false)}
                    title="Set Guard Daily Rate"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Current Daily Rate (PKR)
                            </label>
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                    {guardRate ? guardRate.toLocaleString() : 'Not Set'}
                                </p>
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                New Daily Rate (PKR)
                            </label>
                            <TextField
                                type="number"
                                placeholder="Enter daily rate"
                                value={newRate}
                                onChange={(e) => setNewRate(e.target.value)}
                            />
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Note: Updating the rate will only affect new requests. Existing requests will keep their historical rate.
                            </p>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => setShowRateModal(false)}
                                disabled={settingRate}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdateGuardRate}
                                disabled={settingRate}
                            >
                                {settingRate ? 'Saving...' : 'Save Rate'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Branch Selection Modal */}
            {showBranchModal && (
                <Modal
                    isOpen={showBranchModal}
                    onClose={() => setShowBranchModal(false)}
                    title="Select Branch"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <TextField
                                type="text"
                                placeholder="Search branch by code or address"
                                value={branchSearch}
                                onChange={(e) => setBranchSearch(e.target.value)}
                                prefix={<Icon icon={FiSearch} />}
                            />
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {filteredBranches.length === 0 ? (
                                <p className="py-4 text-center text-gray-500 dark:text-gray-400">No branches found</p>
                            ) : (
                                <div className="space-y-2">
                                    {filteredBranches.map((branch) => (
                                        <button
                                            key={branch.id}
                                            type="button"
                                            onClick={() => handleBranchSelection(branch)}
                                            className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                                        >
                                            <div className="font-semibold text-gray-900 dark:text-gray-100">{branch.branchCode}</div>
                                            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                {branch.address || 'No address available'}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Company Selection Modal */}
            {showCompanyModal && (
                <Modal
                    isOpen={showCompanyModal}
                    onClose={() => setShowCompanyModal(false)}
                    title={`Select ${formData.requestType} Company`}
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <TextField
                                type="text"
                                placeholder="Search company by name or ID"
                                value={companySearch}
                                onChange={(e) => setCompanySearch(e.target.value)}
                                prefix={<Icon icon={FiSearch} />}
                            />
                        </div>
                        <div className="mb-3">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Found {availableCompanies.length} companies for {formData.requestType} requests
                            </p>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {availableCompanies.length === 0 ? (
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                    <p className="text-sm text-blue-900 dark:text-blue-100">
                                        No {formData.requestType} companies found. Please add companies first.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {availableCompanies
                                        .filter(company =>
                                            company.companyName?.toLowerCase().includes(companySearch.toLowerCase()) ||
                                            company.companyId?.toLowerCase().includes(companySearch.toLowerCase())
                                        )
                                        .map((company) => (
                                            <button
                                                key={company.id}
                                                type="button"
                                                onClick={() => handleCompanySelection(company)}
                                                className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                                            >
                                                <div className="font-semibold text-gray-900 dark:text-gray-100">{company.companyName}</div>
                                                <div className="mt-1 space-y-1 text-sm text-gray-500 dark:text-gray-400">
                                                    <div>ID: {company.companyId}</div>
                                                    <div>Type: {company.companyType}</div>
                                                    {company.companyAddress && <div>Address: {company.companyAddress}</div>}
                                                    {company.email && <div>Email: {company.email}</div>}
                                                </div>
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
            {/* Toast */}
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toastMessage}
                            type={toastType}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

